/* Read actual product options from Giftship's same-origin storefront settings. */
(function (global) {
  'use strict';

  if (global.KindredProductOptionCatalog) return;

  var TIMEOUT_MS = 20000;
  var CACHE_MS = 30000;
  var MAX_ACTIVE = 3;
  var groups = ['Size', 'Width', 'Construction', 'Flex', 'Function'];
  var handoffKeys = ['kindred_handoff', 'kindred_shape', 'kindred_size', 'kindred_width', 'kindred_flex', 'kindred_construction', 'kindred_function', 'kindred_product_id'];
  var queue = [];
  var active = 0;
  var pending = new Map();
  var cache = new Map();
  var resources = new Map();
  var uncachedSetupId = 0;

  function emptyGroups() {
    var result = {};
    groups.forEach(function (name) { result[name] = { present: false, values: [], entries: [] }; });
    return result;
  }

  function failure(message) {
    return { ready: false, busy: false, groups: emptyGroups(), signature: '', productId: '', appliedSize: false, error: message };
  }

  function clone(value) { return JSON.parse(JSON.stringify(value)); }

  function normalize(value) {
    return String(value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function bool(value) { return value === true || value === 1 || value === '1' || value === 'true'; }

  function destinationUrl(destination) {
    var url;
    try { url = new URL(String(destination || ''), global.location.href); } catch (error) { return null; }
    if (url.origin !== global.location.origin || !/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/products\/[^/]+\/?$/i.test(url.pathname) || url.username || url.password) return null;
    var current = new URL(global.location.href);
    ['preview_theme_id', 'preview_context', 'preview_key', '_ab', '_fd', '_sc'].forEach(function (name) {
      var value = current.searchParams.get(name);
      var valid = name === 'preview_theme_id' ? /^\d{1,24}$/.test(value || '')
        : name.charAt(0) === '_' ? /^[01]$/.test(value || '')
        : Boolean(value && value.length <= 2048 && !/[\x00-\x1f\x7f<>]/.test(value));
      if (valid) url.searchParams.set(name, value);
    });
    handoffKeys.forEach(function (name) { url.searchParams.delete(name); });
    url.searchParams.delete('kindred_options_probe');
    url.hash = '';
    return url;
  }

  function localeRoot() {
    var route = global.Shopify && global.Shopify.routes && global.Shopify.routes.root || '/';
    try {
      var url = new URL(route, global.location.href);
      if (url.origin !== global.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(url.pathname)) return '/';
      return url.pathname;
    } catch (error) { return '/'; }
  }

  function settingsUrl(productId) {
    var shop = String(global.Shopify && global.Shopify.shop || '');
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shop)) throw new Error('The store identity could not be confirmed.');
    var url = new URL(localeRoot() + 'a/gs/settings/', global.location.href);
    url.searchParams.set('myshopify_url', shop);
    url.searchParams.set('page_type', 'product');
    url.searchParams.set('product_id', productId);
    var locale = global.Shopify && global.Shopify.locale;
    if (locale && /^[a-z]{2}(?:[-_][a-z]{2})?$/i.test(locale)) url.searchParams.set('locale', locale);
    // This is the same storefront preview override used by Giftship itself.
    try {
      var override = global.localStorage.getItem('_gs_override_settings');
      if (override && override.length <= 65536) url.searchParams.set('_gs_override_settings', encodeURIComponent(override));
    } catch (error) { /* Storage can be unavailable in private browsing. */ }
    return url;
  }

  function requestJson(job, url) {
    if (job.done) return Promise.reject(new Error('Product options request ended.'));
    if (typeof global.fetch !== 'function') return Promise.reject(new Error('Product options could not be loaded.'));
    var key = url.href;
    var stored = resources.get(key);
    if (stored && stored.expires > Date.now()) return Promise.resolve(clone(stored.value));
    if (stored) resources.delete(key);
    return global.fetch(key, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: job.abortController && job.abortController.signal
    }).then(function (response) {
      if (!response || !response.ok) throw new Error('The product options service could not be reached.');
      if (response.url) {
        var finalUrl = new URL(response.url, global.location.href);
        if (finalUrl.origin !== global.location.origin) throw new Error('Product options left this store.');
      }
      return response.json();
    }).then(function (value) {
      if (job.done) throw new Error('Product options request ended.');
      if (!value || typeof value !== 'object') throw new Error('The product options response was invalid.');
      resources.set(key, { expires: Date.now() + CACHE_MS, value: clone(value) });
      return value;
    });
  }

  function groupName(block) {
    var settings = block.settings || {};
    var names = [settings.name, settings.label].map(normalize);
    if (names.some(function (name) { return /^(?:function|touring construction|board type|board format)$/.test(name); })) return 'Function';
    if (names.some(function (name) { return /^(?:construction|composite|composite construction|contruction(?: split)?)$/.test(name); })) return 'Construction';
    if (names.some(function (name) { return /^(?:flex|flex profile|stiffness|flex pattern)$/.test(name); })) return 'Flex';
    if (names.some(function (name) { return /^(?:width|waist width)$/.test(name); })) return 'Width';
    if (names.some(function (name) { return /^(?:size|length|board size|ski size)$/.test(name); })) return 'Size';
    return '';
  }

  function decodeLabel(value) {
    var element = global.document.createElement('textarea');
    element.innerHTML = value === undefined || value === null ? '' : String(value);
    return element.value.trim();
  }

  function upsellLabel(value) {
    // Giftship removes these variant placeholders before rendering its choices.
    return decodeLabel(value).split(' - ').filter(function (part) { return part !== 'Default' && part !== 'Default Title'; }).join(' - ');
  }

  function blockEntries(block, liveProducts) {
    var settings = block.settings || {};
    var options = Array.isArray(settings.options) ? settings.options : [];
    return options.map(function (option, index) {
      if (settings.first_disabled && index === 0 && block.type === 'select') return null;
      if (option && typeof option === 'object' && (option.disabled === true || option.available === false || option.hidden === true)) return null;
      if (block.type === 'upsell') {
        if (!option || typeof option !== 'object' || !option.variantId || !option.handle) return null;
        var product = liveProducts[option.handle];
        var variant = product && Array.isArray(product.variants) && product.variants.find(function (item) { return String(item.id) === String(option.variantId); });
        if (!variant || variant.available !== true) return null;
        var currentTitle = product.title || option.title;
        var label = upsellLabel(variant.title && variant.title !== 'Default Title' ? currentTitle + ' - ' + variant.title : currentTitle);
        return label ? { value: String(option.variantId), label: label } : null;
      }
      if (!/^(?:select|radio|checkbox|checkbox_list|button|icon_button|gallery)$/.test(block.type)) return null;
      var label = decodeLabel(option && typeof option === 'object' ? option.label || option.value || option.title : option);
      var value = option && typeof option === 'object' && option.value !== undefined ? String(option.value) : label;
      return label && value ? { value: value, label: label } : null;
    }).filter(Boolean);
  }

  function matches(name, wanted, actual) {
    var api = global.KindredGiftshipHandoff;
    if (api && typeof api.matchesOption === 'function') return api.matchesOption(name === 'Flex' ? 'Flex Profile' : name, wanted, actual);
    return normalize(wanted) === normalize(actual);
  }

  function actualValue(snapshot, name, wanted) {
    var group = snapshot.groups[name];
    return group && group.values.find(function (actual) { return matches(name, wanted, actual); }) || '';
  }

  function compareCondition(rule, selection) {
    var key = rule.option || rule.element;
    var selected = selection[String(key || '')] || selection[normalize(key)];
    var values = selected ? [selected.label, selected.value] : [];
    var wanted = rule.value === undefined ? '' : String(rule.value);
    var kind = String(rule.condition || '');
    if (kind === 'IS_SET') return values.length > 0;
    if (kind === 'IS_NOT_SET') return values.length === 0;
    if (kind === 'EQUALS') return values.some(function (value) { return value === wanted || value !== '' && wanted !== '' && Number.isFinite(Number(value)) && Number.isFinite(Number(wanted)) && Number(value) === Number(wanted); });
    if (kind === 'NOT_EQUALS') return !compareCondition({ option: key, condition: 'EQUALS', value: wanted }, selection);
    if (kind === 'CONTAINS') return values.some(function (value) { return value.indexOf(wanted) !== -1; });
    if (kind === 'NOT_CONTAINS') return !compareCondition({ option: key, condition: 'CONTAINS', value: wanted }, selection);
    if (kind === 'GREATER_THAN' || kind === 'LESS_THAN') return values.some(function (value) { return value !== '' && wanted !== '' && Number.isFinite(Number(value)) && Number.isFinite(Number(wanted)) && (kind === 'GREATER_THAN' ? Number(value) > Number(wanted) : Number(value) < Number(wanted)); });
    throw new Error('A product option rule needs review on the product page.');
  }

  function visibleBlock(block, selection) {
    if (block.enabled === false || block.settings && block.settings.enabled === false) return false;
    var conditions = block.conditions || {};
    var type = conditions.display_rule_type || 'always';
    var rules = conditions.display_rules || [];
    var cartRules = conditions.cart_display_rules || [];
    if (type === 'always' || !rules.length && !cartRules.length) return true;
    // Recommendations must never infer availability from unsupported cart rules.
    if (cartRules.length) throw new Error('A product option depends on the current cart. Please review the product page.');
    var results = rules.map(function (rule) { return compareCondition(rule, selection); });
    if (type === 'any') return results.some(Boolean);
    if (type === 'all') return results.every(Boolean);
    throw new Error('A product option rule needs review on the product page.');
  }

  function nativeSizeEntries(product) {
    var option = (product.options || []).find(function (item) { return /^(?:size|length)$/i.test(String(item && item.name || item)); });
    if (!option) return [];
    var index = product.options.indexOf(option);
    var values = [];
    (product.variants || []).forEach(function (variant) {
      if (variant.available !== true) return;
      var label = String(variant.options && variant.options[index] || variant['option' + (index + 1)] || '');
      if (label && values.indexOf(label) === -1) values.push(label);
    });
    return values.map(function (value) { return { value: value, label: value }; });
  }

  function snapshotFor(job, blocks, liveProducts, product, selection) {
    var result = { ready: false, busy: false, groups: emptyGroups(), productId: String(product.id), appliedSize: false, error: '', source: 'giftship-settings' };
    blocks.forEach(function (block) {
      var name = groupName(block);
      if (!name || !visibleBlock(block, selection)) return;
      var group = result.groups[name];
      group.present = true;
      blockEntries(block, liveProducts).forEach(function (entry) {
        if (group.values.indexOf(entry.label) !== -1) return;
        group.values.push(entry.label);
        group.entries.push(entry);
      });
    });
    // Giftship owns a group when configured, even if its choices are unavailable.
    if (!result.groups.Size.present && !blocks.some(function (block) { return groupName(block) === 'Size'; })) {
      var nativeEntries = nativeSizeEntries(product);
      result.groups.Size = { present: nativeEntries.length > 0, values: nativeEntries.map(function (entry) { return entry.label; }), entries: nativeEntries };
    }
    result.ready = result.groups.Size.values.length > 0;
    result.appliedSize = Boolean(job.size && actualValue(result, 'Size', job.size));
    result.signature = JSON.stringify(result.groups);
    return result;
  }

  function selectField(name, label, snapshot, blocks, liveProducts, selection) {
    var actual = snapshot.groups[name] && snapshot.groups[name].values.indexOf(label) !== -1 ? label : '';
    if (!actual) throw new Error('The selected ' + name.toLowerCase() + ' is not currently available.');
    var entry = snapshot.groups[name].entries.find(function (item) { return item.label === actual; });
    selection[name] = entry;
    selection[normalize(name)] = entry;
    blocks.forEach(function (block) {
      if (groupName(block) !== name || !visibleBlock(block, selection) || !blockEntries(block, liveProducts).some(function (item) { return item.label === actual; })) return;
      selection[String(block.id)] = entry;
      selection[normalize(block.settings && block.settings.name)] = entry;
      selection[normalize(block.settings && block.settings.label)] = entry;
    });
  }

  async function readSettings(job) {
    var productUrl = new URL(job.url);
    productUrl.pathname = productUrl.pathname.replace(/\/$/, '') + '.js';
    var product = await requestJson(job, productUrl);
    if (!/^\d+$/.test(String(product.id || '')) || !Array.isArray(product.variants)) throw new Error('The destination product could not be confirmed.');
    if (job.expectedProductId && String(product.id) !== job.expectedProductId) throw new Error('The product options belong to another product.');
    var settings = await requestJson(job, settingsUrl(String(product.id)));
    if (settings.request && settings.request.product_id && String(settings.request.product_id) !== String(product.id)) throw new Error('The product options belong to another product.');
    if (!Array.isArray(settings.product_options) || !settings.options || !settings.options.product_options) throw new Error('Product options were not supplied by the store.');
    var enabled = bool(settings.options.enabled) && bool(settings.options.product_options.enable);
    var sets = enabled ? settings.product_options.filter(function (set) { return set.enabled !== false && (!set.display_settings || set.display_settings.enabled !== false); }) : [];
    var blocks = [];
    sets.forEach(function (set) { (Array.isArray(set.blocks) ? set.blocks : []).forEach(function (block) { if (groupName(block)) blocks.push(block); }); });
    var handles = [];
    blocks.forEach(function (block) {
      if (block.type !== 'upsell') return;
      (block.settings.options || []).forEach(function (option) {
        if (option && /^[a-z0-9][a-z0-9-]*$/i.test(option.handle || '') && handles.indexOf(option.handle) === -1) handles.push(option.handle);
      });
    });
    var liveProducts = {};
    await Promise.all(handles.map(async function (handle) {
      var url = new URL(localeRoot() + 'products/' + handle + '.js', global.location.href);
      var data = await requestJson(job, url);
      if (data.handle && data.handle !== handle || !Array.isArray(data.variants)) throw new Error('An offered product option could not be confirmed.');
      liveProducts[handle] = data;
    }));
    var selection = {};
    var snapshot = snapshotFor(job, blocks, liveProducts, product, selection);
    var functionGroup = snapshot.groups.Function;
    if (job.productType !== 'skis' && functionGroup.present) {
      var functionValue = actualValue(snapshot, 'Function', job.productType === 'splitboard' ? 'Split' : 'Solid');
      if (!functionValue) throw new Error('The requested board format is not currently available.');
      selectField('Function', functionValue, snapshot, blocks, liveProducts, selection);
      snapshot = snapshotFor(job, blocks, liveProducts, product, selection);
    }
    var sizeValue = job.size ? actualValue(snapshot, 'Size', job.size) : '';
    if (!sizeValue) return snapshot;
    selectField('Size', sizeValue, snapshot, blocks, liveProducts, selection);
    snapshot = snapshotFor(job, blocks, liveProducts, product, selection);
    if (job.chooseSetup) {
      var setup = job.chooseSetup(clone(snapshot)) || {};
      if (typeof setup !== 'object' || typeof setup.then === 'function') throw new Error('The product setup must be chosen synchronously.');
      ['Width', 'Construction'].forEach(function (name) {
        var requested = setup[name.toLowerCase()];
        if (requested === undefined || requested === null || requested === '') return;
        selectField(name, String(requested), snapshot, blocks, liveProducts, selection);
        snapshot = snapshotFor(job, blocks, liveProducts, product, selection);
      });
    }
    ['Function', 'Size', 'Width', 'Construction'].forEach(function (name) {
      var chosen = selection[name];
      if (chosen && snapshot.groups[name].values.indexOf(chosen.label) === -1) throw new Error('The recommended product setup is no longer available together.');
    });
    if (!snapshot.ready || !actualValue(snapshot, 'Size', job.size)) throw new Error('The selected size is no longer available.');
    snapshot.appliedSize = true;
    return snapshot;
  }

  function start(job) {
    job.started = true;
    active += 1;
    job.timeout = global.setTimeout(function () { finish(job, failure('Product options took too long to load. Please try again.')); }, TIMEOUT_MS);
    readSettings(job).then(function (result) { finish(job, result); }).catch(function (error) { finish(job, failure(error && error.message || 'Product options could not be read.')); });
  }

  function processQueue() {
    while (active < MAX_ACTIVE && queue.length) {
      var job = queue.shift();
      if (!job.done) start(job);
    }
  }

  function finish(job, result) {
    if (job.done) return;
    job.done = true;
    global.clearTimeout(job.timeout);
    if (job.abortController) job.abortController.abort();
    if (job.started) active -= 1;
    pending.delete(job.key);
    if (result.ready && !result.error) cache.set(job.key, { expires: Date.now() + CACHE_MS, snapshot: clone(result) });
    job.resolve(result);
    processQueue();
  }

  function load(destination, options) {
    var url = destinationUrl(destination);
    if (!url) return Promise.resolve(failure('Only product pages in this store can supply product options.'));
    var input = options || {};
    var productType = input.productType === 'splitboard' ? 'splitboard' : input.productType === 'skis' || input.productType === 'ski' ? 'skis' : 'snowboard';
    var size = input.size === undefined || input.size === null ? '' : String(input.size);
    var expectedProductId = input.productId === undefined || input.productId === null ? '' : String(input.productId);
    var chooseSetup = typeof input.chooseSetup === 'function' ? input.chooseSetup : null;
    var setupKey = !chooseSetup ? ['none'] : input.setupKey === undefined || input.setupKey === null ? ['uncached', ++uncachedSetupId] : ['setup', String(input.setupKey)];
    var key = JSON.stringify([url.href, productType, size, expectedProductId, setupKey]);
    var cached = cache.get(key);
    if (cached && cached.expires > Date.now()) return Promise.resolve(clone(cached.snapshot));
    if (cached) cache.delete(key);
    if (pending.has(key)) return pending.get(key);
    var job = { url: url.href, key: key, productType: productType, size: size, expectedProductId: expectedProductId, chooseSetup: chooseSetup, done: false, started: false };
    if (typeof global.AbortController === 'function') job.abortController = new global.AbortController();
    var promise = new Promise(function (resolve) { job.resolve = resolve; });
    pending.set(key, promise);
    queue.push(job);
    processQueue();
    return promise;
  }

  global.KindredProductOptionCatalog = { load: load };
})(window);
